export interface ShippingAddress {
  street: string;
  houseNumber: string;
  houseNumberExt?: string;
  postalCode: string;
  city: string;
  countryCode: string;
  contactName?: string;
}

/** Valid PostNL ShipmentType values for customs declarations */
export type PostNLShipmentType =
  | 'Gift'
  | 'Documents'
  | 'Commercial Goods'
  | 'Commercial Sample'
  | 'Returned Goods';

export type PostNLPackageType = 'mailbox' | 'package';

export interface PostNLProductCodes {
  package_nl?: string;
  package_be?: string;
  package_eu?: string;
  package_non_eu?: string;
  mailbox_nl?: string;
  mailbox_be?: string;
  mailbox_eu?: string;
  mailbox_non_eu?: string;
  insured_nl?: string;
  insured_be?: string;
  insured_eu?: string;
  insured_non_eu?: string;
  signature_nl?: string;
  signature_be?: string;
  signature_eu?: string;
  signature_non_eu?: string;
  evening_nl?: string;
  sunday_nl?: string;
}

export interface SenderAddress extends ShippingAddress {
  isConfigured: boolean;
  /** Insurance value in EUR per item for PostNL labels. Default 5 when missing. */
  insurancePerItem?: number;
  /** Manual SDR rate: 1 EUR = X SDR for PostNL non-EU insured value. Default 1 when missing. */
  insuranceSdrRate?: number;
  /** PostNL customs ShipmentType. Default: 'Commercial Goods' */
  shipmentType?: PostNLShipmentType;
  /** Default PostNL pack type for EU shipments. Default: 'package' */
  defaultPackageType?: PostNLPackageType;
  /** When true, non-EU (GlobalPack) shipments use insured product 4947 with Insurance per item. Default: false. */
  globalpackInsurance?: boolean;
  /** Value split for non-EU labels: 1=full, 2=1/2, 3=1/3, 4=1/4, 5=1/5. Default: 1. */
  valueSplit?: 1 | 2 | 3 | 4 | 5;
  /** OpenAI API key for AI Address Assistant */
  openaiApiKey?: string;
  /** Whether AI Address Assistant is enabled globally */
  aiAddressEnabled?: boolean;
  /** Per-admin PostNL product code overrides */
  postnlProductCodes?: PostNLProductCodes;
}

export interface RecipientAddress {
  name: string;
  street: string;
  houseNumber: string;
  houseNumberExt?: string;
  /** Extra address details (e.g. building/block/apartment). */
  addressExtra?: string;
  postalCode: string;
  city: string;
  countryCode: string;
  phone?: string;
  /** State/Province/Region (required for some destinations like US/CA). */
  region?: string;
}

/**
 * Where returns ship back to, from `users.warehouse_*`. Distinct from
 * `SenderAddress` (the PostNL sender) and from the company letterhead address.
 */
export interface WarehouseAddress extends ShippingAddress {
  name?: string;
  phone?: string;
  email?: string;
  isConfigured: boolean;
}

/**
 * Sender override for a return label. Absent means the label is blind and the
 * server falls back to `users.shipping_*` exactly as an outbound label does.
 */
export interface ReturnLabelSender {
  name: string;
  street: string;
  houseNumber: string;
  houseNumberExt?: string;
  postalCode: string;
  city: string;
  countryCode: string;
  phone?: string;
}

export type LabelKind = 'outbound' | 'return';

export type LabelStatus = 'generated' | 'printed' | 'cancelled';

export interface ShippingLabel {
  id: string;
  admin_id: string;
  created_by: string;
  recipient_name: string;
  recipient_street: string;
  recipient_house_number: string;
  recipient_house_number_ext?: string;
  recipient_postal_code: string;
  recipient_city: string;
  recipient_country_code: string;
  reference?: string;
  barcode: string;
  tracking_code: string;
  label_pdf_url?: string;
  label_pdf_base64?: string;
  status: LabelStatus;
  print_count: number;
  last_printed_at?: string;
  last_printed_by?: string;
  created_at: string;
  updated_at: string;
  creator?: { full_name: string; email: string };
  /** 'outbound' on every pre-existing row (column default). */
  label_kind?: LabelKind;
  /** Who was printed as sender on a customer-sender return. Null for blind labels. */
  sender_snapshot?: ReturnLabelSender | null;
}

export interface CreateLabelRequest {
  recipient: RecipientAddress;
  reference?: string;
  shopifyOrderId?: number;
  /** Server re-reads `users.warehouse_*` as the recipient and skips all outbound side effects. */
  isReturn?: boolean;
  /** Only honoured with `isReturn`; overrides the `users.shipping_*` sender. */
  sender?: ReturnLabelSender;
}

export interface CreateLabelResponse {
  success: boolean;
  label?: ShippingLabel;
  barcode?: string;
  trackingCode?: string;
  trackingUrl?: string;
  pdfBase64?: string;
  shopifySync?: ShopifyLabelSyncResult;
  error?: string;
}

export interface ShopifyLabelSyncResult {
  attempted: boolean;
  success: boolean;
  shopifyOrderId: number;
  shopifyFulfilled: boolean;
  customerNotified: boolean;
  trackingUpdated: boolean;
  fulfillmentIdNumeric?: number | null;
  error?: string | null;
  code?: string;
  retryable?: boolean;
}

export interface LabelHistoryFilters {
  search?: string;
  status?: LabelStatus | 'all';
  dateRange?: 'all' | 'today' | 'week' | 'month' | 'custom';
  startDate?: string;
  endDate?: string;
}

export interface LabelHistoryResponse {
  labels: ShippingLabel[];
  total: number;
  page: number;
  pageSize: number;
}

export interface PostalCodeFormat {
  pattern: RegExp;
  format: string;
  example: string;
  placeholder: string;
}

export const POSTAL_CODE_FORMATS: Record<string, PostalCodeFormat> = {
  NL: {
    pattern: /^\d{4}\s?[A-Z]{2}$/i,
    format: '1234 AB',
    example: '1234 AB',
    placeholder: '1234 AB',
  },
  BE: {
    pattern: /^\d{4}$/,
    format: '1234',
    example: '1000',
    placeholder: '1234',
  },
  DE: {
    pattern: /^\d{5}$/,
    format: '12345',
    example: '10115',
    placeholder: '12345',
  },
  FR: {
    pattern: /^\d{5}$/,
    format: '12345',
    example: '75001',
    placeholder: '12345',
  },
  LU: {
    pattern: /^\d{4}$/,
    format: '1234',
    example: '1234',
    placeholder: '1234',
  },
  AT: {
    pattern: /^\d{4}$/,
    format: '1234',
    example: '1010',
    placeholder: '1234',
  },
  ES: {
    pattern: /^\d{5}$/,
    format: '12345',
    example: '28001',
    placeholder: '12345',
  },
  IT: {
    pattern: /^\d{5}$/,
    format: '12345',
    example: '00100',
    placeholder: '12345',
  },
  PT: {
    pattern: /^\d{4}(-\d{3})?$/,
    format: '1234-567',
    example: '1000-001',
    placeholder: '1234-567',
  },
  GB: {
    pattern: /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i,
    format: 'SW1A 1AA',
    example: 'SW1A 1AA',
    placeholder: 'SW1A 1AA',
  },
  CH: {
    pattern: /^\d{4}$/,
    format: '1234',
    example: '8001',
    placeholder: '1234',
  },
  PL: {
    pattern: /^\d{2}-\d{3}$/,
    format: '12-345',
    example: '00-001',
    placeholder: '12-345',
  },
  CZ: {
    pattern: /^\d{3}\s?\d{2}$/,
    format: '123 45',
    example: '110 00',
    placeholder: '123 45',
  },
  DK: {
    pattern: /^\d{4}$/,
    format: '1234',
    example: '1050',
    placeholder: '1234',
  },
  SE: {
    pattern: /^\d{3}\s?\d{2}$/,
    format: '123 45',
    example: '111 21',
    placeholder: '123 45',
  },
  NO: {
    pattern: /^\d{4}$/,
    format: '1234',
    example: '0150',
    placeholder: '1234',
  },
  FI: {
    pattern: /^\d{5}$/,
    format: '12345',
    example: '00100',
    placeholder: '12345',
  },
  IE: {
    pattern: /^[A-Z\d]{3}\s?[A-Z\d]{4}$/i,
    format: 'A12 BC34',
    example: 'D02 AF30',
    placeholder: 'A12 BC34',
  },
  GR: {
    pattern: /^\d{3}\s?\d{2}$/,
    format: '123 45',
    example: '104 31',
    placeholder: '123 45',
  },
  HU: {
    pattern: /^\d{4}$/,
    format: '1234',
    example: '1011',
    placeholder: '1234',
  },
  US: {
    pattern: /^\d{5}(-\d{4})?$/,
    format: '12345 or 12345-6789',
    example: '10001',
    placeholder: '12345',
  },
  CA: {
    pattern: /^[A-Z]\d[A-Z]\s?\d[A-Z]\d$/i,
    format: 'A1A 1A1',
    example: 'M5V 3L9',
    placeholder: 'A1A 1A1',
  },
  AU: {
    pattern: /^\d{4}$/,
    format: '1234',
    example: '2000',
    placeholder: '1234',
  },
  LV: {
    pattern: /^(LV-)?\d{4}$/i,
    format: 'LV-1234',
    example: 'LV-1073',
    placeholder: 'LV-1234',
  },
};

// Some destinations do not use postal codes (or they are commonly absent in Shopify).
// We treat postal code as optional for these, and also allow missing house numbers.
export const COUNTRIES_WITHOUT_POSTAL_CODES = new Set([
  'AE', 'HK', 'QA', 'YE', 'KP', 'MO', 'AO', 'BW', 'GH', 'UG',
  'CM', 'BI', 'BJ', 'BS', 'BZ', 'AW', 'CW', 'BO', 'FJ', 'TO', 'TV',
]);

export function isPostalCodeRequired(countryCode: string): boolean {
  const cc = (countryCode || '').trim().toUpperCase();
  if (!cc) return true;
  return !COUNTRIES_WITHOUT_POSTAL_CODES.has(cc);
}

export function isHouseNumberRequired(countryCode: string): boolean {
  const cc = (countryCode || '').trim().toUpperCase();
  if (!cc) return true;
  return !COUNTRIES_WITHOUT_POSTAL_CODES.has(cc);
}

export function isRegionRequired(countryCode: string): boolean {
  const cc = (countryCode || '').trim().toUpperCase();
  return cc === 'US' || cc === 'CA';
}

export function validatePostalCode(postalCode: string, countryCode: string): { valid: boolean; error?: string } {
  const format = POSTAL_CODE_FORMATS[countryCode];
  if (!format) {
    return { valid: true };
  }

  const normalized = postalCode.trim().toUpperCase();
  if (!format.pattern.test(normalized)) {
    return {
      valid: false,
      error: `Invalid format. Expected: ${format.format} (e.g., ${format.example})`,
    };
  }

  return { valid: true };
}

export function detectCountryFromPostalCode(postalCode: string): { code: string; name: string; confidence: 'high' | 'medium' | 'low' } | null {
  const normalized = postalCode.trim().toUpperCase();
  if (!normalized) return null;

  if (/^\d{4}\s?[A-Z]{2}$/i.test(normalized)) {
    return { code: 'NL', name: 'Netherlands', confidence: 'high' };
  }

  if (/^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i.test(normalized)) {
    return { code: 'GB', name: 'United Kingdom', confidence: 'high' };
  }

  if (/^\d{4}-\d{3}$/.test(normalized)) {
    return { code: 'PT', name: 'Portugal', confidence: 'high' };
  }

  // NOTE: 5-digit postal codes are used by many countries (DE/FR/ES/US/IT/SE/RS/MX/etc.).
  // Any attempt to guess a specific country from a 5-digit number creates false warnings.
  // We intentionally do not suggest a country based on 5 digits alone.

  if (/^\d{4}$/.test(normalized)) {
    const num = parseInt(normalized, 10);
    if (num >= 1000 && num <= 9999) {
      // 4-digit postal codes are very common worldwide (AU, AT, CH, DK, HU, NZ, ZA, etc.)
      // so we treat BE detection as low-confidence to avoid false mismatch warnings.
      return { code: 'BE', name: 'Belgium', confidence: 'low' };
    }
  }

  return null;
}

export function getPostalCodeMismatchWarning(
  postalCode: string,
  selectedCountryCode: string
): { suggestedCountry: { code: string; name: string }; message: string } | null {
  const detected = detectCountryFromPostalCode(postalCode);
  if (!detected) return null;

  if (detected.code === selectedCountryCode) return null;

  if (detected.confidence === 'low') return null;

  const selectedCountry = SUPPORTED_COUNTRIES.find(c => c.code === selectedCountryCode);
  const selectedName = selectedCountry?.name || selectedCountryCode;

  return {
    suggestedCountry: { code: detected.code, name: detected.name },
    message: `This postal code looks like ${detected.name} format. You selected ${selectedName}. Did you mean ${detected.name}?`
  };
}

export type SupportedCountry = { code: string; name: string };

// Full ISO-3166 alpha-2 list (English names). Keep in sync with select usage so
// Shopify `countryCodeV2` never silently falls back to a default.
export const SUPPORTED_COUNTRIES: SupportedCountry[] = [
  { code: "AF", name: "Afghanistan" },
  { code: "AX", name: "Åland Islands" },
  { code: "AL", name: "Albania" },
  { code: "DZ", name: "Algeria" },
  { code: "AS", name: "American Samoa" },
  { code: "AD", name: "Andorra" },
  { code: "AO", name: "Angola" },
  { code: "AI", name: "Anguilla" },
  { code: "AQ", name: "Antarctica" },
  { code: "AG", name: "Antigua and Barbuda" },
  { code: "AR", name: "Argentina" },
  { code: "AM", name: "Armenia" },
  { code: "AW", name: "Aruba" },
  { code: "AU", name: "Australia" },
  { code: "AT", name: "Austria" },
  { code: "AZ", name: "Azerbaijan" },
  { code: "BS", name: "Bahamas" },
  { code: "BH", name: "Bahrain" },
  { code: "BD", name: "Bangladesh" },
  { code: "BB", name: "Barbados" },
  { code: "BY", name: "Belarus" },
  { code: "BE", name: "Belgium" },
  { code: "BZ", name: "Belize" },
  { code: "BJ", name: "Benin" },
  { code: "BM", name: "Bermuda" },
  { code: "BT", name: "Bhutan" },
  { code: "BO", name: "Bolivia, Plurinational State of" },
  { code: "BQ", name: "Bonaire, Sint Eustatius and Saba" },
  { code: "BA", name: "Bosnia and Herzegovina" },
  { code: "BW", name: "Botswana" },
  { code: "BV", name: "Bouvet Island" },
  { code: "BR", name: "Brazil" },
  { code: "IO", name: "British Indian Ocean Territory" },
  { code: "BN", name: "Brunei Darussalam" },
  { code: "BG", name: "Bulgaria" },
  { code: "BF", name: "Burkina Faso" },
  { code: "BI", name: "Burundi" },
  { code: "KH", name: "Cambodia" },
  { code: "CM", name: "Cameroon" },
  { code: "CA", name: "Canada" },
  { code: "CV", name: "Cape Verde" },
  { code: "KY", name: "Cayman Islands" },
  { code: "CF", name: "Central African Republic" },
  { code: "TD", name: "Chad" },
  { code: "CL", name: "Chile" },
  { code: "CN", name: "China" },
  { code: "CX", name: "Christmas Island" },
  { code: "CC", name: "Cocos (Keeling) Islands" },
  { code: "CO", name: "Colombia" },
  { code: "KM", name: "Comoros" },
  { code: "CG", name: "Congo" },
  { code: "CD", name: "Congo, the Democratic Republic of the" },
  { code: "CK", name: "Cook Islands" },
  { code: "CR", name: "Costa Rica" },
  { code: "CI", name: "Côte d'Ivoire" },
  { code: "HR", name: "Croatia" },
  { code: "CU", name: "Cuba" },
  { code: "CW", name: "Curaçao" },
  { code: "CY", name: "Cyprus" },
  { code: "CZ", name: "Czech Republic" },
  { code: "DK", name: "Denmark" },
  { code: "DJ", name: "Djibouti" },
  { code: "DM", name: "Dominica" },
  { code: "DO", name: "Dominican Republic" },
  { code: "EC", name: "Ecuador" },
  { code: "EG", name: "Egypt" },
  { code: "SV", name: "El Salvador" },
  { code: "GQ", name: "Equatorial Guinea" },
  { code: "ER", name: "Eritrea" },
  { code: "EE", name: "Estonia" },
  { code: "ET", name: "Ethiopia" },
  { code: "FK", name: "Falkland Islands (Malvinas)" },
  { code: "FO", name: "Faroe Islands" },
  { code: "FJ", name: "Fiji" },
  { code: "FI", name: "Finland" },
  { code: "FR", name: "France" },
  { code: "GF", name: "French Guiana" },
  { code: "PF", name: "French Polynesia" },
  { code: "TF", name: "French Southern Territories" },
  { code: "GA", name: "Gabon" },
  { code: "GM", name: "Gambia" },
  { code: "GE", name: "Georgia" },
  { code: "DE", name: "Germany" },
  { code: "GH", name: "Ghana" },
  { code: "GI", name: "Gibraltar" },
  { code: "GR", name: "Greece" },
  { code: "GL", name: "Greenland" },
  { code: "GD", name: "Grenada" },
  { code: "GP", name: "Guadeloupe" },
  { code: "GU", name: "Guam" },
  { code: "GT", name: "Guatemala" },
  { code: "GG", name: "Guernsey" },
  { code: "GN", name: "Guinea" },
  { code: "GW", name: "Guinea-Bissau" },
  { code: "GY", name: "Guyana" },
  { code: "HT", name: "Haiti" },
  { code: "HM", name: "Heard Island and McDonald Islands" },
  { code: "VA", name: "Holy See (Vatican City State)" },
  { code: "HN", name: "Honduras" },
  { code: "HK", name: "Hong Kong" },
  { code: "HU", name: "Hungary" },
  { code: "IS", name: "Iceland" },
  { code: "IN", name: "India" },
  { code: "ID", name: "Indonesia" },
  { code: "IR", name: "Iran, Islamic Republic of" },
  { code: "IQ", name: "Iraq" },
  { code: "IE", name: "Ireland" },
  { code: "IM", name: "Isle of Man" },
  { code: "IL", name: "Israel" },
  { code: "IT", name: "Italy" },
  { code: "JM", name: "Jamaica" },
  { code: "JP", name: "Japan" },
  { code: "JE", name: "Jersey" },
  { code: "JO", name: "Jordan" },
  { code: "KZ", name: "Kazakhstan" },
  { code: "KE", name: "Kenya" },
  { code: "KI", name: "Kiribati" },
  { code: "KP", name: "Korea, Democratic People's Republic of" },
  { code: "KR", name: "Korea, Republic of" },
  { code: "KW", name: "Kuwait" },
  { code: "KG", name: "Kyrgyzstan" },
  { code: "LA", name: "Lao People's Democratic Republic" },
  { code: "LV", name: "Latvia" },
  { code: "LB", name: "Lebanon" },
  { code: "LS", name: "Lesotho" },
  { code: "LR", name: "Liberia" },
  { code: "LY", name: "Libya" },
  { code: "LI", name: "Liechtenstein" },
  { code: "LT", name: "Lithuania" },
  { code: "LU", name: "Luxembourg" },
  { code: "MO", name: "Macao" },
  { code: "MK", name: "Macedonia, the Former Yugoslav Republic of" },
  { code: "MG", name: "Madagascar" },
  { code: "MW", name: "Malawi" },
  { code: "MY", name: "Malaysia" },
  { code: "MV", name: "Maldives" },
  { code: "ML", name: "Mali" },
  { code: "MT", name: "Malta" },
  { code: "MH", name: "Marshall Islands" },
  { code: "MQ", name: "Martinique" },
  { code: "MR", name: "Mauritania" },
  { code: "MU", name: "Mauritius" },
  { code: "YT", name: "Mayotte" },
  { code: "MX", name: "Mexico" },
  { code: "FM", name: "Micronesia, Federated States of" },
  { code: "MD", name: "Moldova, Republic of" },
  { code: "MC", name: "Monaco" },
  { code: "MN", name: "Mongolia" },
  { code: "ME", name: "Montenegro" },
  { code: "MS", name: "Montserrat" },
  { code: "MA", name: "Morocco" },
  { code: "MZ", name: "Mozambique" },
  { code: "MM", name: "Myanmar" },
  { code: "NA", name: "Namibia" },
  { code: "NR", name: "Nauru" },
  { code: "NP", name: "Nepal" },
  { code: "NL", name: "Netherlands" },
  { code: "NC", name: "New Caledonia" },
  { code: "NZ", name: "New Zealand" },
  { code: "NI", name: "Nicaragua" },
  { code: "NE", name: "Niger" },
  { code: "NG", name: "Nigeria" },
  { code: "NU", name: "Niue" },
  { code: "NF", name: "Norfolk Island" },
  { code: "MP", name: "Northern Mariana Islands" },
  { code: "NO", name: "Norway" },
  { code: "OM", name: "Oman" },
  { code: "PK", name: "Pakistan" },
  { code: "PW", name: "Palau" },
  { code: "PS", name: "Palestine, State of" },
  { code: "PA", name: "Panama" },
  { code: "PG", name: "Papua New Guinea" },
  { code: "PY", name: "Paraguay" },
  { code: "PE", name: "Peru" },
  { code: "PH", name: "Philippines" },
  { code: "PN", name: "Pitcairn" },
  { code: "PL", name: "Poland" },
  { code: "PT", name: "Portugal" },
  { code: "PR", name: "Puerto Rico" },
  { code: "QA", name: "Qatar" },
  { code: "RE", name: "Réunion" },
  { code: "RO", name: "Romania" },
  { code: "RU", name: "Russian Federation" },
  { code: "RW", name: "Rwanda" },
  { code: "BL", name: "Saint Barthélemy" },
  { code: "SH", name: "Saint Helena, Ascension and Tristan da Cunha" },
  { code: "KN", name: "Saint Kitts and Nevis" },
  { code: "LC", name: "Saint Lucia" },
  { code: "MF", name: "Saint Martin (French part)" },
  { code: "PM", name: "Saint Pierre and Miquelon" },
  { code: "VC", name: "Saint Vincent and the Grenadines" },
  { code: "WS", name: "Samoa" },
  { code: "SM", name: "San Marino" },
  { code: "ST", name: "Sao Tome and Principe" },
  { code: "SA", name: "Saudi Arabia" },
  { code: "SN", name: "Senegal" },
  { code: "RS", name: "Serbia" },
  { code: "SC", name: "Seychelles" },
  { code: "SL", name: "Sierra Leone" },
  { code: "SG", name: "Singapore" },
  { code: "SX", name: "Sint Maarten (Dutch part)" },
  { code: "SK", name: "Slovakia" },
  { code: "SI", name: "Slovenia" },
  { code: "SB", name: "Solomon Islands" },
  { code: "SO", name: "Somalia" },
  { code: "ZA", name: "South Africa" },
  { code: "GS", name: "South Georgia and the South Sandwich Islands" },
  { code: "SS", name: "South Sudan" },
  { code: "ES", name: "Spain" },
  { code: "LK", name: "Sri Lanka" },
  { code: "SD", name: "Sudan" },
  { code: "SR", name: "Suriname" },
  { code: "SJ", name: "Svalbard and Jan Mayen" },
  { code: "SZ", name: "Swaziland" },
  { code: "SE", name: "Sweden" },
  { code: "CH", name: "Switzerland" },
  { code: "SY", name: "Syrian Arab Republic" },
  { code: "TW", name: "Taiwan, Province of China" },
  { code: "TJ", name: "Tajikistan" },
  { code: "TZ", name: "Tanzania, United Republic of" },
  { code: "TH", name: "Thailand" },
  { code: "TL", name: "Timor-Leste" },
  { code: "TG", name: "Togo" },
  { code: "TK", name: "Tokelau" },
  { code: "TO", name: "Tonga" },
  { code: "TT", name: "Trinidad and Tobago" },
  { code: "TN", name: "Tunisia" },
  { code: "TR", name: "Turkey" },
  { code: "TM", name: "Turkmenistan" },
  { code: "TC", name: "Turks and Caicos Islands" },
  { code: "TV", name: "Tuvalu" },
  { code: "UG", name: "Uganda" },
  { code: "UA", name: "Ukraine" },
  { code: "AE", name: "United Arab Emirates" },
  { code: "GB", name: "United Kingdom" },
  { code: "US", name: "United States" },
  { code: "UM", name: "United States Minor Outlying Islands" },
  { code: "UY", name: "Uruguay" },
  { code: "UZ", name: "Uzbekistan" },
  { code: "VU", name: "Vanuatu" },
  { code: "VE", name: "Venezuela, Bolivarian Republic of" },
  { code: "VN", name: "Viet Nam" },
  { code: "VG", name: "Virgin Islands, British" },
  { code: "VI", name: "Virgin Islands, U.S." },
  { code: "WF", name: "Wallis and Futuna" },
  { code: "EH", name: "Western Sahara" },
  { code: "YE", name: "Yemen" },
  { code: "ZM", name: "Zambia" },
  { code: "ZW", name: "Zimbabwe" },
  // Common non-ISO code used by some systems (e.g. Shopify) for Kosovo.
  { code: "XK", name: "Kosovo" },
];

export function getSupportedCountriesWithFallback(selectedCountryCode: string | null | undefined): SupportedCountry[] {
  const normalized = (selectedCountryCode || "").trim().toUpperCase();
  if (!normalized) return SUPPORTED_COUNTRIES;
  if (SUPPORTED_COUNTRIES.some((c) => c.code === normalized)) return SUPPORTED_COUNTRIES;
  return [{ code: normalized, name: `Unknown (${normalized})` }, ...SUPPORTED_COUNTRIES];
}
